/**
 * The Composer's paste rules, kept apart from the component so the
 * decision and the naming are testable without rendering it.
 */

/** What a browser calls a file it made from the clipboard. */
const GENERIC_CLIPBOARD_NAME = /^image\.[a-z0-9]+$/i;

export const hasGenericClipboardName = (file: File): boolean =>
  GENERIC_CLIPBOARD_NAME.test(file.name);

export type PasteOutcome = { kind: 'text' } | { kind: 'files'; files: File[] };

/**
 * Text wins, from `text/plain` and the file names alone. Text next to
 * files that all carry the generic name is a picture of that text (Excel,
 * Word), so the text is pasted and nothing is attached.
 */
export const decidePaste = (textPlain: string, files: File[]): PasteOutcome =>
  files.length === 0 ||
  (textPlain.trim() !== '' && files.every(hasGenericClipboardName))
    ? { kind: 'text' }
    : { kind: 'files', files };

/**
 * The clipboard's files: `items` of kind `file` first, then `files`, as the
 * window drop reads a drag, but it also falls back to `files` when items exist
 * and none is a file. Never reads `text/html`: its images are remote links,
 * and reading them would make a paste fetch any URL the page names.
 */
export const readClipboardFiles = (data: DataTransfer): File[] => {
  const fromItems: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems.length > 0 ? fromItems : Array.from(data.files ?? []);
};

const pad = (value: number, length = 2) => String(value).padStart(length, '0');

const EXTENSION_BY_MIME = { 'image/png': 'png', 'image/jpeg': 'jpg' } as const;

export type PastedImageMime = keyof typeof EXTENSION_BY_MIME;

export const isPastedImageMime = (type: string): type is PastedImageMime =>
  type in EXTENSION_BY_MIME;

/**
 * `<translated stem>.<ext>` on the local clock, `YYYY-MM-DD` and
 * `HH.MM.SS` (a colon is not safe in a file name).
 */
export const pastedImageName = (
  now: Date,
  mime: PastedImageMime,
  translate: (params: { date: string; time: string }) => string,
): string => {
  const date = `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(now.getSeconds())}`;
  return `${translate({ date, time })}.${EXTENSION_BY_MIME[mime]}`;
};

/** `name`, or `stem (2).ext`, `stem (3).ext` … until it is not in `taken`. */
export const withUniqueName = (
  name: string,
  taken: readonly string[],
): string => {
  if (!taken.includes(name)) return name;
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const extension = dot > 0 ? name.slice(dot) : '';
  for (let n = 2; ; n++) {
    const candidate = `${stem} (${n})${extension}`;
    if (!taken.includes(candidate)) return candidate;
  }
};
